import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography,
    Row,
    Col,
    Divider,
    Tag,
    message,
    Modal
} from 'antd';
import apiClient from 'services/apiClient';
import {
    CheckOutlined,
    ArrowLeftOutlined,
    CalendarOutlined,
    CreditCardOutlined,
    GiftOutlined,
    InfoCircleOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { Shield } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { pricingStyles } from '../../Pricing/pricingStyles';
import { modernPlanStyles } from './modernPlanStyles';
import { useMyContext } from 'Context/MyContextProvider';
import LoginForm from '../../Auth/LoginForm';

const { Title, Text } = Typography;

export default function PlanDetailsPage() {
    const location = useLocation();
    const { selectedPlan, isYearly } = location.state || {};
    const { api, authToken, isLoggedIn } = useMyContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [calculations, setCalculations] = useState({});
    const [showLoginModal, setShowLoginModal] = useState(false);

    // Calculate dates and pricing
    const calculatePlanDetails = useCallback(() => {
        if (!selectedPlan) return;

        const startDate = new Date();
        const endDate = new Date();

        if (isYearly) {
            endDate.setFullYear(endDate.getFullYear() + 1);
        } else {
            endDate.setMonth(endDate.getMonth() + 1);
        }

        const basePrice = isYearly ? selectedPlan.yearly_price : selectedPlan.monthly_price;
        const gstRate = 18; // 18% GST
        const gstAmount = Math.round((basePrice * gstRate) / 100);
        const totalAmount = Number(basePrice) + Number(gstAmount);

        // Discount calculation for yearly plans
        const discount = isYearly ? (selectedPlan.monthly_price * 12 - selectedPlan.yearly_price) : 0;
        const discountPercentage = isYearly ? Math.round((discount / (selectedPlan.monthly_price * 12)) * 100) : 0;

        setCalculations({
            startDate: startDate.toLocaleDateString('en-IN'),
            endDate: endDate.toLocaleDateString('en-IN'),
            basePrice,
            gstAmount,
            totalAmount,
            discount,
            discountPercentage,
            billingCycle: isYearly ? 'Yearly' : 'Monthly',
            nextBillingDate: endDate.toLocaleDateString('en-IN')
        });
    }, [selectedPlan, isYearly]);

    useEffect(() => {
        calculatePlanDetails();
    }, [calculatePlanDetails]);

    // Close login modal when user successfully logs in
    useEffect(() => {
        if (isLoggedIn && authToken && showLoginModal) {
            setShowLoginModal(false);
            message.success('Login successful! You can now proceed with your purchase.');
        }
    }, [isLoggedIn, authToken, showLoginModal]);

    const handleProceedToPayment = async () => {
        // Check if user is logged in
        if (!isLoggedIn || !authToken) {
            setShowLoginModal(true);
            return;
        }

        setLoading(true);
        
        try {
            const purchaseData = {
                plan_id: selectedPlan.id,
                billing_cycle: isYearly ? 'yearly' : 'monthly',
                base_price: calculations.basePrice,
                gst_amount: calculations.gstAmount,
                total_amount: calculations.totalAmount,
                discount: calculations.discount || 0,
                start_date: calculations.startDate,
                end_date: calculations.endDate,
                payment_gateway:'test',
            };

            const response = await apiClient.post(`${api}plans/purchase`, purchaseData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                }
            });

            const result = response.data;

            if (response.status === 200 && result.status) {
                message.success('Plan purchase initiated successfully!');
                
                // Navigate to payment gateway or success page
                if (result.payment_url) {
                    window.location.href = result.payment_url;
                } else {
                    navigate('/payment-success', { 
                        state: { 
                            purchaseId: result.purchase_id,
                            plan: selectedPlan,
                            calculations 
                        } 
                    });
                }
            } else {
                message.error(result.message || 'Failed to initiate plan purchase');
            }
        } catch (error) {
            console.error('Error purchasing plan:', error);
            message.error('An error occurred while processing your request');
        } finally {
            setLoading(false);
        }
    };

    if (!selectedPlan) {
        return (
                <motion.div
                    style={modernPlanStyles.container}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                >
                    <div style={{ textAlign: 'center', color: 'white', padding: '4rem' }}>
                        <Title level={2} style={{ color: 'white' }}>No plan selected</Title>
                        <button
                            onClick={() => navigate('/pricing')}
                            style={modernPlanStyles.backToPlansButton}
                        >
                            Back to Plans
                        </button>
                    </div>
                </motion.div>
        );
    }
    const HandleLogin = (status) => {
        console.log('login status:', status);
    }
    return (
            <motion.div
                style={modernPlanStyles.container}
                variants={modernPlanStyles.containerVariants}
                initial="hidden"
                animate="visible"
            >

                {/* Header Section */}
                <motion.div
                    style={modernPlanStyles.header}
                    variants={modernPlanStyles.cardVariants}
                >
                    <Title level={1} style={modernPlanStyles.headerTitle}>
                        Plan Details & Checkout
                    </Title>
                    <Text style={modernPlanStyles.headerSubtitle}>
                        Review your selected plan and complete your purchase
                    </Text>
                </motion.div>

                <div style={modernPlanStyles.contentContainer}>
                    <Row gutter={[32, 32]}>
                        {/* Left Column - Plan Details */}
                        <Col xs={24} lg={16}>
                            <motion.div variants={modernPlanStyles.cardVariants}>
                                {/* Plan Header Card */}
                                <div style={modernPlanStyles.planCard}>
                                    <div style={modernPlanStyles.planHeader}>
                                        {selectedPlan.recommended === 1 && (
                                            <span style={modernPlanStyles.planBadge}>
                                                Most Popular
                                            </span>
                                        )}
                                        <Title level={2} style={modernPlanStyles.planTitle}>
                                            {selectedPlan.title} Plan
                                        </Title>
                                        <Text style={modernPlanStyles.planSubtitle}>
                                            {selectedPlan.description || `Perfect for ${selectedPlan.title?.toLowerCase()} users seeking powerful features and flexibility.`}
                                        </Text>
                                    </div>

                                    <div style={modernPlanStyles.priceSection}>
                                        <Title level={1} style={modernPlanStyles.price}>
                                            ₹{calculations.basePrice}
                                        </Title>
                                       
                                        <Text style={modernPlanStyles.period}>
                                            {isYearly ? '/year' : '/month'}
                                        </Text>
                                        {calculations.discount > 0 && (
                                            <div style={modernPlanStyles.discountBadge}>
                                                Save {calculations.discountPercentage}%
                                            </div>
                                        )}
                                    </div>

                                    {/* Features Section */}
                                    <div style={modernPlanStyles.featuresSection}>
                                        <Title level={3} style={modernPlanStyles.sectionTitle}>
                                            <Shield size={20} />
                                            What's Included
                                        </Title>
                                        <div style={modernPlanStyles.featuresGrid}>
                                            {selectedPlan.feature_data?.map((feature, index) => (
                                                <motion.div
                                                    key={index}
                                                    style={modernPlanStyles.featureCard}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.3 + index * 0.1 }}
                                                    whileHover={{ y: -4, transition: { duration: 0.2 } }}
                                                >
                                                    <Text>
                                                        <Tag color="#e8f7ed" className='border-0 rounded-circle'>
                                                            <CheckOutlined style={pricingStyles.featureTag} />
                                                        </Tag>
                                                        {feature?.name}
                                                    </Text>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Timeline Card */}
                            <motion.div variants={modernPlanStyles.cardVariants}>
                                <div style={modernPlanStyles.timelineCard}>
                                    <Title level={3} style={modernPlanStyles.sectionTitle}>
                                        <CalendarOutlined />
                                        Plan Timeline
                                    </Title>
                                    <div style={modernPlanStyles.timelineGrid}>
                                        <motion.div
                                            style={modernPlanStyles.timelineItem}
                                            whileHover={{ y: -4 }}
                                        >
                                            <div style={{
                                                ...modernPlanStyles.timelineIcon,
                                                background: 'linear-gradient(135deg, #4ade80, #22c55e)'
                                            }}>
                                                <CalendarOutlined />
                                            </div>
                                            <div style={modernPlanStyles.timelineTitle}>Plan Activation</div>
                                            <div style={modernPlanStyles.timelineDate}>{calculations.startDate}</div>
                                        </motion.div>
                                        <motion.div
                                            style={modernPlanStyles.timelineItem}
                                            whileHover={{ y: -4 }}
                                        >
                                            <div style={{
                                                ...modernPlanStyles.timelineIcon,
                                                background: 'linear-gradient(135deg, #06b6d4, #0891b2)'
                                            }}>
                                                <CreditCardOutlined />
                                            </div>
                                            <div style={modernPlanStyles.timelineTitle}>Next Billing</div>
                                            <div style={modernPlanStyles.timelineDate}>{calculations.nextBillingDate}</div>
                                        </motion.div>
                                        <motion.div
                                            style={modernPlanStyles.timelineItem}
                                            whileHover={{ y: -4 }}
                                        >
                                            <div style={{
                                                ...modernPlanStyles.timelineIcon,
                                                background: 'linear-gradient(135deg, #f59e0b, #d97706)'
                                            }}>
                                                <ReloadOutlined />
                                            </div>
                                            <div style={modernPlanStyles.timelineTitle}>Auto Renewal</div>
                                            <div style={modernPlanStyles.timelineDate}>{calculations.endDate}</div>
                                        </motion.div>
                                    </div>
                                </div>
                            </motion.div>
                        </Col>

                        {/* Right Column - Billing Summary */}
                        <Col xs={24} lg={8}>
                            <motion.div variants={modernPlanStyles.cardVariants}>
                                {/* Billing Summary */}
                                <div style={modernPlanStyles.billingSummaryCard}>
                                    <Title level={3} style={modernPlanStyles.summaryTitle}>
                                        <CreditCardOutlined />
                                        Billing Summary
                                    </Title>

                                    <div style={modernPlanStyles.summaryRow}>
                                        <span style={modernPlanStyles.summaryLabel}>Plan Price ({calculations.billingCycle})</span>
                                        <span style={modernPlanStyles.summaryValue}>₹{calculations.basePrice}</span>
                                    </div>

                                    {calculations.discount > 0 && (
                                        <div style={{ ...modernPlanStyles.summaryRow, ...modernPlanStyles.discountRow }}>
                                            <span style={modernPlanStyles.summaryLabel}>
                                                <GiftOutlined style={{ marginRight: 8 }} />
                                                Yearly Discount ({calculations.discountPercentage}%)
                                            </span>
                                            <span style={modernPlanStyles.summaryValue}>-₹{calculations.discount}</span>
                                        </div>
                                    )}

                                    <div style={modernPlanStyles.summaryRow}>
                                        <span style={modernPlanStyles.summaryLabel}>GST (18%)</span>
                                        <span style={modernPlanStyles.summaryValue}>₹{calculations.gstAmount}</span>
                                    </div>
                                    <Divider variant="dashed" dashed />
                                    <motion.div
                                        style={modernPlanStyles.totalSection}
                                        initial={{ scale: 0.95 }}
                                        animate={{ scale: 1 }}
                                        transition={{ delay: 0.5, type: "spring" }}
                                    >
                                        <div style={modernPlanStyles.summaryRow}>
                                            <span style={modernPlanStyles.totalLabel}>Total Amount</span>
                                            <span style={modernPlanStyles.totalAmount}>₹{calculations.totalAmount}</span>
                                        </div>
                                    </motion.div>
                                    {/* Action Buttons */}
                                    <div style={modernPlanStyles.securityAlert}>
                                        <Text style={modernPlanStyles.securityText}>
                                            🔒 Your payment information is encrypted and secure. Cancel anytime.
                                        </Text>
                                    </div>
                                </div>
                            </motion.div>
                            <motion.div variants={modernPlanStyles.cardVariants}>
                                <div style={modernPlanStyles.actionButtons}>
                                    <motion.button
                                        style={modernPlanStyles.proceedButton}
                                        whileHover={{ scale: 1.02, y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleProceedToPayment}
                                        disabled={loading}
                                    >
                                        <CreditCardOutlined style={{ marginRight: 8 }} />
                                        {loading ? 'Processing...' : 'Proceed to Payment'}
                                    </motion.button>

                                    <motion.button
                                        style={modernPlanStyles.backToPlansButton}
                                        whileHover={{ scale: 1.02, y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => navigate(-1)}
                                    >
                                        <ArrowLeftOutlined style={{ marginRight: 8 }} />
                                        Back to Plans
                                    </motion.button>
                                </div>
                            </motion.div>

                            {/* Next Steps */}
                            <motion.div variants={modernPlanStyles.cardVariants}>
                                <div style={modernPlanStyles.stepsCard}>
                                    <Title level={3} style={modernPlanStyles.summaryTitle}>
                                        Next Steps
                                    </Title>

                                    <div style={{ ...modernPlanStyles.stepItem, background: 'rgba(74, 222, 128, 0.15)' }}>
                                        <div style={{ ...modernPlanStyles.stepNumber, ...modernPlanStyles.stepCompleted }}>
                                            <CheckOutlined style={{color:'#fff'}}/>
                                        </div>
                                        <div style={modernPlanStyles.stepContent}>
                                            <div style={modernPlanStyles.stepTitle}>Review Plan Details</div>
                                            <div style={modernPlanStyles.stepDescription}>Verify your selected plan and features</div>
                                        </div>
                                    </div>

                                    <div style={{ ...modernPlanStyles.stepItem, background: 'rgba(59, 130, 246, 0.15)' }}>
                                        <div style={{ ...modernPlanStyles.stepNumber, ...modernPlanStyles.stepActive }}>2</div>
                                        <div style={modernPlanStyles.stepContent}>
                                            <div style={modernPlanStyles.stepTitle}>Payment Information</div>
                                            <div style={modernPlanStyles.stepDescription}>Secure payment processing</div>
                                        </div>
                                    </div>

                                    <div style={modernPlanStyles.stepItem}>
                                        <div style={{ ...modernPlanStyles.stepNumber, ...modernPlanStyles.stepPending }}>3</div>
                                        <div style={modernPlanStyles.stepContent}>
                                            <div style={modernPlanStyles.stepTitle}>Account Setup</div>
                                            <div style={modernPlanStyles.stepDescription}>Instant activation of your plan</div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </Col>
                    </Row>

                    {/* Bottom Info Section */}
                    <motion.div
                        style={modernPlanStyles.infoSection}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                    >
                        <div style={modernPlanStyles.infoCard}>
                            <Title level={3} style={{ ...modernPlanStyles.summaryTitle, textAlign: 'center', marginBottom: '2rem' }}>
                                Important Information
                            </Title>
                            <div style={modernPlanStyles.infoGrid}>
                                <motion.div
                                    style={modernPlanStyles.infoItem}
                                    whileHover={{ y: -4 }}
                                >
                                    <CalendarOutlined style={{ fontSize: '2rem', color: '#4ade80', marginBottom: '1rem' }} />
                                    <div style={modernPlanStyles.infoItemTitle}>Billing Cycle</div>
                                    <div style={modernPlanStyles.infoItemText}>
                                        You will be charged {calculations.billingCycle?.toLowerCase()} starting {calculations.startDate}
                                    </div>
                                </motion.div>
                                <motion.div
                                    style={modernPlanStyles.infoItem}
                                    whileHover={{ y: -4 }}
                                >
                                    <InfoCircleOutlined style={{ fontSize: '2rem', color: '#06b6d4', marginBottom: '1rem' }} />
                                    <div style={modernPlanStyles.infoItemTitle}>Cancellation</div>
                                    <div style={modernPlanStyles.infoItemText}>
                                        You can cancel your subscription anytime. No cancellation fees.
                                    </div>
                                </motion.div>
                                <motion.div
                                    style={modernPlanStyles.infoItem}
                                    whileHover={{ y: -4 }}
                                >
                                    <Shield size={32} style={{ color: '#f59e0b', marginBottom: '1rem' }} />
                                    <div style={modernPlanStyles.infoItemTitle}>Support</div>
                                    <div style={modernPlanStyles.infoItemText}>
                                        24/7 customer support included with all plans.
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Login Modal */}
                <Modal
                    title="Sign In Required"
                    open={showLoginModal}
                    onCancel={() => setShowLoginModal(false)}
                    footer={null}
                    destroyOnClose
                    style={{ top: 20 }}
                >
                        <LoginForm isModal={true} HandleModalLogin={HandleLogin} />
                </Modal>

                {/* Global Styles */}
                <style jsx>{`
                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-10px); }
                    }
                    
                    button {
                        border: none;
                        cursor: pointer;
                    }
                    
                    button:disabled {
                        opacity: 0.7;
                        cursor: not-allowed;
                    }
                `}</style>
            </motion.div>
    );
}
